import { useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, X, Trash2, Layers, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { buildVariantMatrix } from '../data/store';

let fieldSeq = 0;
// Variant fields are always rendered as radio selectors.
const newField = () => ({ id: `vf-new-${++fieldSeq}`, label: '', type: 'radio', options: [] });

// Dynamic n-field variant builder. Each field = label + type + n option values.
export const VariantBuilder = ({ fields, onChange, accent }) => {
  const [optionDrafts, setOptionDrafts] = useState({}); // fieldId -> in-progress option text

  const update = (next) => onChange(next);

  const addField = () => update([...(fields || []), newField()]);
  const removeField = (idx) => update((fields || []).filter((_, i) => i !== idx));
  const patchField = (idx, patch) => update((fields || []).map((f, i) => (i === idx ? { ...f, ...patch } : f)));

  const addOption = (idx) => {
    const text = (optionDrafts[idx] || '').trim();
    if (!text) return;
    
    // Split by comma to allow batch adding like "1G, 2G, 3G"
    const newOptions = text.split(',').map((s) => s.trim()).filter(Boolean);
    if (newOptions.length === 0) return;

    const f = fields[idx];
    
    // Filter out options that already exist in the current field
    const uniqueNewOptions = newOptions.filter(
      (newOpt) => !f.options.some((existing) => existing.toLowerCase() === newOpt.toLowerCase())
    );

    // Remove duplicates within the typed string itself
    const finalOptions = [...new Set(uniqueNewOptions)];

    if (finalOptions.length > 0) {
      patchField(idx, { options: [...f.options, ...finalOptions] });
    }
    
    setOptionDrafts((d) => ({ ...d, [idx]: '' }));
  };

  const removeOption = (fieldIdx, optIdx) => {
    const f = fields[fieldIdx];
    patchField(fieldIdx, { options: f.options.filter((_, i) => i !== optIdx) });
  };

  const comboCount = buildVariantMatrix(fields || []).length;
  const hasFields = (fields && fields.length > 0);

  return (
    <div className="flex flex-col bg-slate-50/50 h-full"> 
      <div className="h-[53px] px-5 flex items-center justify-between shrink-0 border-b border-[var(--theme-tertiary)] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)] relative z-10">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-slate-600">
          Variant fields
        </p>
        {hasFields && (
          <span 
            className="text-[10px] font-extrabold uppercase tracking-[0.14em]"
            style={{ color: accent }}
          >
            {comboCount} combination{comboCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="flex flex-col p-5 gap-4">
        <AnimatePresence initial={false}>
          {hasFields ? (
            fields.map((f, idx) => (
              <motion.div 
                key={f.id || idx}
                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden relative group/field"
              >
                <div className="p-4 pt-3.5 border-b border-slate-100 bg-slate-50/50 flex flex-col gap-1.5">
                  <div className="flex items-center justify-between pr-1">
                    <label className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-500 pl-1">Variant Name</label>
                    <button
                      type="button"
                      onClick={() => removeField(idx)}
                      aria-label="Remove field"
                      className="text-slate-400 hover:text-rose-500 transition-colors p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={f.label}
                    onChange={(e) => patchField(idx, { label: e.target.value })}
                    placeholder="e.g. Size, Color, Material"
                    className="w-full max-w-sm bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-extrabold text-slate-800 placeholder:text-slate-300 outline-none focus:border-[var(--theme-primary)] focus:ring-2 focus:ring-[var(--theme-primary-soft)] transition-all shadow-sm"
                  />
                </div>

                {/* Options Area */}
                <div className="p-4 pt-3 flex flex-col gap-2">
                  <div className="flex items-center gap-1.5 pl-1">
                    <label className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-500">Option Values</label>
                    <div className="group relative flex items-center justify-center text-slate-300 hover:text-slate-500 transition-colors cursor-help">
                      <HelpCircle size={14} />
                      <div className="absolute left-0 bottom-full mb-2 w-56 p-2.5 bg-slate-800 text-white text-[11px] font-medium rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20 shadow-lg pointer-events-none normal-case tracking-normal leading-relaxed">
                        e.g., If Variant Name is "Weight", you can type <strong className="text-emerald-400">1kg, 2kg, 3kg</strong> with commas and press Enter to add them all at once!
                        <div className="absolute left-2 top-full w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-slate-800"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <AnimatePresence>
                      {f.options.map((opt, optIdx) => (
                        <motion.span
                          key={`${f.id || idx}-${optIdx}`}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-lg text-[12px] font-bold shadow-sm border group/chip transition-colors"
                          style={{ background: 'var(--theme-primary-soft)', borderColor: 'var(--theme-primary-tint)', color: accent }}
                        >
                          {opt}
                          <button
                             type="button"
                             onClick={() => removeOption(idx, optIdx)}
                             aria-label={`Remove ${opt}`}
                             className="flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity ml-1"
                          >
                             <X size={12} />
                          </button>
                        </motion.span>
                      ))}
                    </AnimatePresence>

                    <div className="relative flex items-center mt-1">
                      <input
                        type="text"
                        value={optionDrafts[idx] || ''}
                        onChange={(e) => setOptionDrafts({ ...optionDrafts, [idx]: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOption(idx))}
                        onBlur={() => addOption(idx)}
                        placeholder="Type value & press Enter..."
                        className="w-48 bg-slate-50 text-[12px] font-bold text-slate-600 placeholder:text-slate-400 outline-none border border-slate-200 rounded-lg px-3 py-2 focus:border-[var(--theme-primary)] focus:bg-white focus:text-[#0d0d0d] transition-all shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              className="py-10 flex flex-col items-center justify-center text-center"
            >
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-300 mb-3 shadow-inner">
                <Layers size={20} />
              </div>
              <h4 className="text-[13px] font-bold text-slate-600 mb-1">No variants defined</h4>
              <p className="text-[11px] font-medium text-slate-400 max-w-xs mb-5">
                Add fields like Size or Color to create different variations of this product.
              </p>
              <button
                type="button"
                onClick={addField}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-sm transition-all hover:shadow hover:-translate-y-0.5"
                style={{ backgroundColor: accent }}
              >
                <Plus size={14} aria-hidden="true" /> Add First Variant
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {hasFields && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button
              type="button"
              onClick={addField}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed text-[11px] font-extrabold uppercase tracking-[0.14em] transition-all hover:bg-slate-50 hover:border-solid"
              style={{ borderColor: 'var(--theme-primary-tint)', color: accent }}
            >
              <Plus size={14} aria-hidden="true" /> Add another variant field
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

VariantBuilder.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string),
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  accent: PropTypes.string.isRequired,
};
